/**
 * The Schwartz circumplex wheel — the interview's reward artifact (§5c, V4.2). An
 * inline-SVG value wheel: the ten basic values in the validated circular order, each
 * drawn as a wedge filled to its prognosed magnitude and coloured by its higher-order
 * region, with a compact region legend beneath. Confidence is rendered HONESTLY — a
 * low-confidence sector is muted and hatched (»noch offen«), so the wheel never overclaims
 * a reading the interview has not yet sharpened.
 *
 * Shared module: rendered as the interview's completion reward (with the model's German
 * insight) AND on /compass/werte. Theme-aware via the surface tokens (light DOM — the
 * `.sw-*` CSS in index.html reaches it); no new dependencies. The sector order + labels +
 * region mapping come from `@career/definition-v2` (one source of truth with the api-v2
 * prognosis composer) — the profile's own sectors are already in circumplex order.
 */

import { html, svg, classMap, nothing, type TemplateResult } from "@platform/web-kit/render"
import { SCHWARTZ_REGION_LABEL_DE, type SchwartzProfile, type SchwartzSector } from "@career/definition-v2"

/**
 * The smallest magnitude that still draws. A wedge of zero length reads as »you do not
 * hold this value«, which in a theory where everyone holds all ten is the strongest
 * over-claim the wheel could make — and it fired for any value simply absent from the
 * eight cards a person picked out of 62. Every sector is drawn; a low one is drawn short.
 */
const MIN_VISIBLE_MAG = 0.085

/** The four higher-order regions in the wheel's arc order (matches the sector order). */
const REGION_ORDER = ["openness_to_change", "self_enhancement", "conservation", "self_transcendence"] as const

const CX = 160
const CY = 160
const INNER_R = 32
const MAX_R = 118
const TRACK_R = 120
const LABEL_R = MAX_R + 16
const GAP_DEG = 1.6
const SECTOR_DEG = 36 // 360 / 10

/** Low-confidence threshold — below this a sector reads »noch offen« (muted + hatched). */
const LOW_CONFIDENCE = 0.3

function polar(r: number, deg: number): [number, number] {
  const a = (deg * Math.PI) / 180
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)]
}

/** An annular-sector (donut wedge) path from `rInner` to `rOuter` over [startDeg, endDeg]. */
function annularSector(rInner: number, rOuter: number, startDeg: number, endDeg: number): string {
  const [x1, y1] = polar(rOuter, startDeg)
  const [x2, y2] = polar(rOuter, endDeg)
  const [x3, y3] = polar(rInner, endDeg)
  const [x4, y4] = polar(rInner, startDeg)
  const large = endDeg - startDeg > 180 ? 1 : 0
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${rOuter} ${rOuter} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} L ${x3.toFixed(2)} ${y3.toFixed(2)} A ${rInner} ${rInner} 0 ${large} 0 ${x4.toFixed(2)} ${y4.toFixed(2)} Z`
}

/** The center angle of sector `i` — index 0 (self_direction) at the top, going clockwise. */
const centerAngle = (i: number): number => -90 + i * SECTOR_DEG

function sectorWedge(sector: SchwartzSector, i: number): TemplateResult {
  const start = centerAngle(i) - SECTOR_DEG / 2 + GAP_DEG / 2
  const end = centerAngle(i) + SECTOR_DEG / 2 - GAP_DEG / 2
  const mag = Math.max(MIN_VISIBLE_MAG, Math.min(1, sector.magnitude))
  const fillR = INNER_R + mag * (MAX_R - INNER_R)
  const low = sector.confidence < LOW_CONFIDENCE
  const opacity = 0.4 + 0.55 * Math.max(0, Math.min(1, sector.confidence))
  const track = svg`<path class="sw-track" d=${annularSector(INNER_R, TRACK_R, start, end)} />`
  const fill = svg`<path
          class=${classMap({ "sw-fill": true, [`sw-region-${sector.higherOrder}`]: true, "is-low": low })}
          d=${annularSector(INNER_R, fillR, start, end)}
          style=${`opacity:${opacity.toFixed(3)}`}
        />${low ? svg`<path class="sw-hatch" d=${annularSector(INNER_R, fillR, start, end)} />` : nothing}`
  return svg`${track}${fill}`
}

/**
 * The person's OWN average, drawn as a reference circle. The card game is a forced
 * ranking of eight values out of 62, so a radius says how a value sits against that
 * person's other values — it is not a score on an absolute scale, and without a visible
 * baseline it invites being read as one. With the ring, a short wedge reads »below your
 * own middle«, which is what the data supports.
 */
function neutralRing(sectors: readonly SchwartzSector[]): TemplateResult | typeof nothing {
  if (sectors.length === 0) return nothing
  const mean = sectors.reduce((sum, s) => sum + Math.max(0, Math.min(1, s.magnitude)), 0) / sectors.length
  const r = INNER_R + mean * (MAX_R - INNER_R)
  return svg`<circle class="sw-mean" cx=${CX} cy=${CY} r=${r.toFixed(2)} />`
}

function sectorLabel(sector: SchwartzSector, i: number): TemplateResult {
  const [x, y] = polar(LABEL_R, centerAngle(i))
  const cos = Math.cos((centerAngle(i) * Math.PI) / 180)
  const anchor = Math.abs(cos) < 0.34 ? "middle" : cos > 0 ? "start" : "end"
  return svg`<text class="sw-sector-label" x=${x.toFixed(1)} y=${y.toFixed(1)} text-anchor=${anchor} dominant-baseline="middle" lang="de">${sector.label}</text>`
}

/**
 * Render the wheel for a predicted profile. `size` sets the rendered box (default 320);
 * `showLegend` adds the four region labels beneath (default true). Returns `nothing`-free
 * markup even for an all-zero profile (an honest empty wheel).
 *
 * THERE IS NO DERIVATION PANEL, and its absence is a decision. A per-sector »wie dieses Bild
 * entstanden ist« listing cards and questions shipped here once: it consumed most of the
 * screen, repeated the same sentence down nearly every sector, and answered a question
 * nobody had asked. The person who prompted it had just spent twenty minutes making these
 * decisions — he knew what he had chosen. What he said was that the RESULT did not
 * reconcile with the experience, and arithmetic shown back to him could not answer that.
 *
 * What answers it is the wheel plus the model's read of it, and honest confidence: a sector
 * the material does not pin renders muted and hatched (»noch offen«) rather than as a
 * certain wedge. `SchwartzSectorBasis` is still composed server-side as the audit trail; it
 * is simply not something a person is made to read.
 */
export function schwartzWheel(
  profile: SchwartzProfile,
  opts: { size?: number; showLegend?: boolean; labels?: { legendTitle?: string } } = {},
): TemplateResult {
  const size = opts.size ?? 320
  const showLegend = opts.showLegend ?? true
  const sectors = profile.sectors
  return html`
    <figure class="sw-wheel" role="img" aria-label="Schwartz-Wertekreis">
      <!-- viewBox padded (-40 -12 → 416×344) so the outer sector labels live INSIDE the box;
           width:100% + max-width lets the whole wheel scale down on narrow screens instead of
           overflowing (the labels used to rely on overflow:visible, which app-wide overflow-x
           clipping then chopped on mobile).

           The right pad is 16 units wider than the left because an SVG CLIPS at its viewBox
           and does so silently: the widest right-hand label needed 363 units against a 360-unit
           box and rendered »Etwas erreicher«, with every layout, overflow and measure assertion
           still green — the text never touched the page's layout to overflow anything. The
           evidence capture now measures each label's own bbox against this box. -->
      <svg viewBox="-40 -12 416 344" width="100%" style=${`max-width:${Math.round(size * 1.3)}px`} class="sw-svg" aria-hidden="true">
        <defs>
          <pattern id="sw-hatch" patternUnits="userSpaceOnUse" width="5" height="5" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="5" class="sw-hatch-line" />
          </pattern>
        </defs>
        <circle class="sw-hub" cx=${CX} cy=${CY} r=${INNER_R} />
        ${sectors.map((s, i) => sectorWedge(s, i))} ${neutralRing(sectors)}
        ${sectors.map((s, i) => sectorLabel(s, i))}
      </svg>
      ${showLegend
        ? html`<figcaption class="sw-legend">
            ${REGION_ORDER.map(
              (r) => html`<span class=${`sw-legend-item sw-region-${r}`}
                ><span class="sw-legend-swatch" aria-hidden="true"></span><span lang="de">${SCHWARTZ_REGION_LABEL_DE[r]}</span></span
              >`,
            )}
          </figcaption>`
        : nothing}
      <!-- One line, and it teaches how to READ the wheel rather than how it was computed:
           without the reference ring a short wedge reads as »you don't have this«, which is
           the strongest over-claim the picture can make. -->
      <p class="sw-ring-note" lang="de">
        Der Ring ist dein eigener Mittelwert — Werte innerhalb davon sind dir im Vergleich zu deinen anderen
        weniger wichtig, nicht unwichtig.
      </p>
    </figure>`
}
